import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, } from "react-router-dom";
import socket from "../../socket";
import api from "../../api/api";

const detectHasLocation = (user) => {
  if (!user) return false;

  // Case 1: New structure
  if (user.location?.city && user.location?.country) return true;

  // Case 2: Old / flat structure
  if (user.city && user.country) return true;

  return false;
};

export const Dashboard = () => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [userId, setUserId] = useState(null);
  const [greeting, setGreeting] = useState("");
  const [userProfile, setUserProfile] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationUpdatePopup, setShowLocationUpdatePopup] = useState(false);
  
  const [loading, setLoading] = useState(false);

  // Dashboard data
  const [metrics, setMetrics] = useState({
    revenueToday: 0,
    pendingOrders: 0,
    dispatchedOrders: 0,
    cancelledOrders: 0,
    topProducts: [],
    recentOrders: []
  });

  const hasLocation =
  !!(
    userProfile?.location?.city ||
    userProfile?.city
  );

  // -------------------------------------------
  // Load Token
  // -------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setUserId(payload.userId);
      } catch(err) {
        console.log(err)
      }
    }
  }, []);

  // -------------------------------------------
  // Socket listener
  // -------------------------------------------
  useEffect(() => {
    if (!userId) return;

    socket.emit("join", userId);

    socket.on("orderUpdate", (data) => {
      setNotifications((prev) => [data, ...prev]);
    });

    return () => socket.off("orderUpdate");
  }, [userId]);

  // -------------------------------------------
  // Dashboard Data Loader
  // -------------------------------------------
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const orderRes = await api.orders.vendorMine();

        if (!orderRes || !Array.isArray(orderRes.orders)) return;

        const orders = orderRes.orders;

        // 1️⃣ Revenue Today
        const isToday = (dateStr) => {
          const d = new Date(dateStr);
          const now = new Date();
          return (
            d.getFullYear() === now.getFullYear() &&
            d.getMonth() === now.getMonth() &&
            d.getDate() === now.getDate()
          );
        };

        const revenueToday = orders
          .filter((o) => o.paymentStatus === "paid" && isToday(o.createdAt))
          .reduce((sum, o) => sum + o.totalAmount, 0);

        // 2️⃣ Pending Count
        const pendingOrders = orders.filter((o) => o.status === "pending").length;


        // 3️⃣ Dispatched Count
        const dispatchedOrders = orders.filter((o) => o.status === "delivered"|| o.status === "shipped").length;

        // 4️⃣ Cancelled Count
        const cancelledOrders = orders.filter((o) => o.status === "cancelled").length;

        // 5️⃣ Top Products
        const productSalesMap = {};

        orders.forEach((order) => {
  if (!Array.isArray(order.items)) return;

  order.items.forEach((item) => {
    if (!item?.productId || !item.productId?._id) return;

    const id = item.productId._id;

    if (!productSalesMap[id]) {
      productSalesMap[id] = {
        name: item.productId.name || item.name || "Unknown product",
        img: item.productId.images?.[0] || null,
        price: item.pricePerUnit || 0,
        sold: 0,
      };
    }

    productSalesMap[id].sold += Number(item.quantity || 0);
  });
});


        const topProducts = Object.values(productSalesMap)
          .sort((a, b) => b.sold - a.sold)
          .slice(0, 3);

        // 6️⃣ Recent Orders
        const recentOrders = [...orders]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5);

        // Save to state
        setMetrics({
          revenueToday,
          pendingOrders,
          dispatchedOrders,
          cancelledOrders,
          topProducts,
          recentOrders,
        });
      } catch (err) {
        console.log("Error loading dashboard:", err);
      }
    };

    loadDashboardData();
  }, []);

useEffect(() => {
  (async () => {
    try {
      setLoading(true);

      const res = await api.profile.viewProfile();
      const user = res?.user || res;

      setUserProfile(user);

      const hasLocation = detectHasLocation(user);

      // 🔑 Always sync localStorage
      localStorage.setItem("hasLocation", hasLocation ? "true" : "false");

    } catch (err) {
      console.error("Failed to load userData:", err?.message || err);
    } finally {
      setLoading(false);
    }
  })();
}, []);



//  useEffect(() => {
//   const hasLocation = localStorage.getItem("hasLocation");

//   if (!hasLocation) {
//     setShowLocationUpdatePopup(true);
//   }
// }, []);

useEffect(() => {
  const cachedHasLocation = localStorage.getItem("hasLocation");

  if (cachedHasLocation === "false") {
    setShowLocationUpdatePopup(true);
  }
}, []);

useEffect(() => {
  if (!loading && userProfile) {
    const hasLocation = detectHasLocation(userProfile);
    setShowLocationUpdatePopup(!hasLocation);
  }
}, [loading, userProfile]);

  useEffect(() => {
  const hour = new Date().getHours();

  if (hour < 12) {
    setGreeting("Good morning");
  } else if (hour < 17) {
    setGreeting("Good afternoon");
  } else {
    setGreeting("Good evening");
  }
}, []);



  return (
    <div className="min-h-screen bg-[#fff8e1]">
    
    {showLocationUpdatePopup && (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
    <div className="bg-white w-[90%] rounded-2xl p-6 shadow-lg text-center">
      <h3 className="text-lg font-semibold mb-2">
        Location not set
      </h3>

      <p className="text-gray-600 mb-4">
        Please update your location to start receiving orders.
      </p>

      <button
        onClick={() => navigate("/update-location")}
        className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg transition"
      >
        Update Location
      </button>
    </div>
    </div>
    )}
      <div
        className="
          mx-auto w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16 pt-10 md:pt-12 xl:pt-16 pb-32 md:pb-40
          [font-family:'Nunito',Helvetica] text-[#5d4037]
          pb-32 md:pb-40
        "
      >
    
        <div className="pt-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img alt="avatar" className="w-9 h-9 rounded-full object-cover"
            src="https://cdn-icons-png.flaticon.com/512/149/149071.png"/>
            <div>
              <p className="font-extrabold leading-tight text-[15px] md:text-base">
                {greeting}
              </p>
              <p className="text-[12px] md:text-[13px] leading-tight">
                Let’s make sales today!
              </p>
            </div>
          </div>

          {/* Notification Bell */}
          <div
            onClick={() => setShowDropdown((prev) => !prev)}
            className="relative w-9 h-9 rounded-full bg-[#eadfb7] grid place-items-center cursor-pointer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
              viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
              <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 7h18s-3 0-3-7"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
            </svg>
            {notifications.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#c2452d] border-2 border-[#fff8e1]" />
            )}
          </div>

          {/* Notification Dropdown */}
          {showDropdown && (
            <div className="absolute top-12 right-0 w-72 bg-white border border-[#eadfb7] rounded-lg shadow-lg z-10">
              <div className="p-2 max-h-64 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="text-center text-sm text-gray-500 py-4">No notifications</p>
                ) : (
                  notifications.map((n, i) => (
                    <div key={i} className="p-2 border-b border-[#eadfb7]">
                      <p className="font-semibold text-[13px]">{n.message}</p>
                      <p className="text-xs text-gray-500">Order ID: {n.orderId}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
  
        {/* --------------------- METRICS --------------------- */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 xl:gap-6">
          <div className="rounded-[12px] bg-[#f7c945] p-4">
            <p className="text-[12px] md:text-[13px] font-extrabold">REVENUE TODAY</p>
            <p className="mt-1 font-extrabold text-[18px] md:text-[20px]">
              ₦{metrics.revenueToday.toLocaleString()}
            </p>
          </div>

          <div className="rounded-[12px] bg-white p-4 border border-[#eadfb7]">
            <p className="text-[12px] font-extrabold">PENDING ORDERS</p>
            <p className="mt-1 text-[14px] md:text-[15px]">• {metrics.pendingOrders}</p>
          </div>

          <div className="rounded-[12px] bg-white p-4 border border-[#eadfb7]">
            <p className="text-[12px] font-extrabold">DISPATCHED</p>
            <p className="mt-1 font-extrabold">{metrics.dispatchedOrders}</p>
          </div>

          <div className="rounded-[12px] bg-white p-4 border border-[#eadfb7]">
            <p className="text-[12px] font-extrabold">CANCELLED</p>
            <p className="mt-1 font-extrabold">{metrics.cancelledOrders}</p>
          </div>
        </div>

       
        <p className="mt-8 font-extrabold">Quick Actions</p>
        <div className="mt-4 flex items-start justify-between gap-6 xl:gap-12">

          <button type="button" onClick={() => navigate("/products/new")} className="flex flex-col items-center w-[110px] md:w-[120px]">
            <div className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            </div>
            <span className="mt-2 text-[13px] md:text-[14px]">Add products</span>
          </button>

          <button type="button" onClick={() => navigate("/orders")} className="flex flex-col items-center w-[110px] md:w-[120px]">
            <div className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                <rect x="3" y="4" width="18" height="14" rx="2"/><path d="M7 8h10M7 12h6"/>
              </svg>
            </div>
            <span className="mt-2 text-[13px] md:text-[14px]">View Order</span>
          </button>

          {/* <button type="button" onClick={() => navigate("/analytics")} className="flex flex-col items-center w-[110px] md:w-[120px]">
            <div className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                <path d="M3 12h4v8H3zM10 8h4v12h-4zM17 4h4v16h-4z"/>
              </svg>
            </div>
            <span className="mt-2 text-[13px] md:text-[14px]">Analytics</span>
          </button> */}

          <button type="button" onClick={() => navigate("/withdraw")} className="flex flex-col items-center w-[110px] md:w-[120px]">
          <div className="w-[64px] h-[64px] md:w-[72px] md:h-[72px] rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
          <path d="M12 8v8M8 12h8" />
          <rect x="3" y="4" width="18" height="16" rx="2" />
          </svg>
          </div>
          <span className="mt-2 text-[13px] md:text-[14px]">Withdraw</span>
          </button>

        </div>

        {/* --------------------- TOP PRODUCTS --------------------- */}
        <p className="mt-8 font-extrabold">Top Products</p>
        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {metrics.topProducts.map((p, i) => (
            <div key={i} className="rounded-[10px] bg-[#ede6c9] px-2 py-2 flex items-center">
              <img alt="" src={p.img} className="w-12 h-12 rounded-[8px] object-cover" />
              <div className="ml-3">
                <p className="font-bold text-[14px] md:text-[15px]">{p.name}</p>
                <p className="text-[12px] md:text-[13px]">₦{p.price.toLocaleString()}</p>
              </div>

              <div className="ml-auto text-right pr-1">
                <p className="font-bold">{p.sold}</p>
                <p className="text-[12px]">Sold</p>
              </div>
            </div>
          ))}

          {metrics.topProducts.length === 0 && (
            <p className="text-gray-500 text-sm">No product sales yet.</p>
          )}
        </div>

        <p className="mt-8 font-extrabold">Recent Orders</p>
        <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {metrics.recentOrders.map((o, i) => (
            <div key={i} className="relative rounded-[12px] bg-[#eadfb7] p-3 flex overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#c2452d]" />
              <div className="flex-1 pl-3">
                <div className="flex items-center justify-between">
                  <p className="font-bold">Order #{o.orderId}</p>
                  <p className="font-bold">₦{o.totalAmount.toLocaleString()}</p>
                </div>

                <p className="text-[12px] md:text-[13px] mt-1">
                  {o.buyerPhone} • {o.items.length} items
                </p>

                <span
  className={`
    inline-block mt-2 text-[12px] px-2 py-1 rounded-full
    ${
      o.status === "pending"
        ? "bg-yellow-100 text-yellow-700"
        : o.status === "delivered"
        ? "bg-green-100 text-green-700"
        : o.status === "cancelled"
        ? "bg-red-100 text-red-700"
        : "bg-gray-100 text-gray-700"
    }
  `}
>
  {o.status}
</span>
              </div>
              </div>
          ))}
         
        </div>
      </div>

      <div className="fixed bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16">
        <div className="px-6 pt-2 pb-4 bg-[#fff8e1]">
          <div className="grid grid-cols-4 gap-10 text-[12px] md:text-[13px] text-[#5d4037]">
       
            <div className="flex flex-col items-center">
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="md:w-[20px] md:h-[20px]" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M3 10.5L12 4l9 6.5" /><path d="M5 10v10h14V10" />
                </svg>
              </div>
              <span className="mt-1 font-semibold">Overview</span>
            </div>
            <button 
              type="button" 
              onClick={() => navigate("/orders")} 
              className="flex flex-col items-center" 
            > 
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="md:w-[20px] md:h-[20px]" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                  <path d="M12 12a4 4 0 100-8 4 4 0 000 8z" />
                  <path d="M20 21v-2a6 6 0 00-6-6H10a6 6 0 00-6 6v2" />
                </svg>
              </div>
              <span className="mt-1 text-[#5d4037]/80">Orders</span>

            </button>
            <button 
              type="button" 
              onClick={() => navigate("/products")} 
              className="flex flex-col items-center" 
            > 
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="md:w-[20px] md:h-[20px]" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                  <path d="M12 12a4 4 0 100-8 4 4 0 000 8z" />
                  <path d="M20 21v-2a6 6 0 00-6-6H10a6 6 0 00-6 6v2" />
                </svg>
              </div>
              <span className="mt-1 text-[#5d4037]/80">Product</span>

            </button>
            <button 
              type="button" 
              onClick={() => navigate("/profile")} 
              className="flex flex-col items-center" 
            > 
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-[12px] bg-white border border-[#eadfb7] grid place-items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" className="md:w-[20px] md:h-[20px]" viewBox="0 0 24 24" fill="none" stroke="#5d4037" strokeWidth="2">
                  <path d="M12 12a4 4 0 100-8 4 4 0 000 8z" />
                  <path d="M20 21v-2a6 6 0 00-6-6H10a6 6 0 00-6 6v2" />
                </svg>
              </div>
              <span className="mt-1 text-[#5d4037]/80">Profile</span>

            </button>
          </div>
        </div>
      
        <div className="h-[22px] bg-[#5d4037] flex items-center justify-center">
          <div className="w-[120px] h-[6px] rounded-full bg-white" />
        </div>
      </div>
    </div>
  );
};
